/**
 * Navbar superior
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Typography
} from '@mui/material';
import {
  NotificationsOutlined,
  AccountCircleOutlined,
  LogoutOutlined
} from '@mui/icons-material';
import atomicLogo from '../../assets/atomic-logo.svg';
import { useAuth } from '../../contexts/AuthContext';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const getUserInitial = () => {
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: '#000000',
        borderBottom: '1px solid rgba(255, 107, 53, 0.1)',
        left: 70,
        width: 'calc(100% - 70px)',
      }}
    >
      <Toolbar>
        {/* Logo */}
        <Box
          component="img"
          src={atomicLogo}
          alt="Atomic.Digital"
          sx={{ height: 35, cursor: 'pointer' }}
          onClick={() => navigate('/app')}
        />

        <Box sx={{ flexGrow: 1 }} />

        {/* Right Side Icons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Notifications */}
          <IconButton
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                color: '#FF6B35',
              }
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsOutlined />
            </Badge>
          </IconButton>

          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                color: '#FF6B35',
              }
            }}
          >
            <Avatar
              src={user?.photoURL || undefined}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: '#FF6B35',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitial()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: '#1a1a1a',
                color: '#FFFFFF',
                border: '1px solid rgba(255, 107, 53, 0.2)',
                mt: 1.5,
                minWidth: 220,
              }
            }}
          >
            {/* Informações do usuário */}
            {user && (
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255, 107, 53, 0.2)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {user.displayName || 'Usuário'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {user.email}
                </Typography>
              </Box>
            )}
            <MenuItem
              onClick={handleMenuClose}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                }
              }}
            >
              <AccountCircleOutlined sx={{ mr: 1, color: '#FF6B35' }} />
              Meu Perfil
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                }
              }}
            >
              <LogoutOutlined sx={{ mr: 1, color: '#FF6B35' }} />
              Sair
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
